import type { Database } from "./types";

type StubError = { message: string; code: string };
type StubResult<T = null> = { data: T; error: StubError };

const STUB_ERROR: StubError = {
  message: "Supabase legacy bridge disabled — pending FastAPI religion",
  code: "LEGACY_BRIDGE_OFF",
};

const stubResult = (): StubResult => ({ data: null, error: { ...STUB_ERROR } });

const TERMINAL_METHODS = new Set([
  "single",
  "maybeSingle",
  "csv",
  "explain",
]);

function makeQueryBuilder(): any {
  const builder: any = new Proxy(function () {}, {
    get(_target, prop) {
      if (prop === "then") {
        return (resolve: (v: StubResult) => unknown) =>
          Promise.resolve(stubResult()).then(resolve);
      }
      if (prop === "catch" || prop === "finally") {
        return (cb: (v: StubResult) => unknown) =>
          (Promise.resolve(stubResult()) as any)[prop](cb);
      }
      if (typeof prop === "string" && TERMINAL_METHODS.has(prop)) {
        return async () => stubResult();
      }
      return () => builder;
    },
    apply() {
      return builder;
    },
  });
  return builder;
}

function makeChannel(): any {
  const channel: any = {
    on: () => channel,
    subscribe: (_cb?: unknown) => ({ unsubscribe: () => {} }),
    unsubscribe: () => {},
    send: async () => stubResult(),
    track: async () => stubResult(),
    untrack: async () => stubResult(),
  };
  return channel;
}

function makeStorageBucket() {
  return {
    upload: async () => stubResult(),
    download: async () => stubResult(),
    remove: async () => stubResult(),
    list: async () => stubResult(),
    getPublicUrl: () => ({ data: { publicUrl: "" } }),
    createSignedUrl: async () => stubResult(),
    createSignedUrls: async () => stubResult(),
    move: async () => stubResult(),
    copy: async () => stubResult(),
  };
}

export const supabase: any = {
  from: (_table: string) => makeQueryBuilder(),
  rpc: (_fn: string, _args?: unknown) => {
    return Promise.resolve(stubResult());
  },
  auth: {
    getUser: async () => ({ data: { user: null }, error: { ...STUB_ERROR } }),
    getSession: async () => ({
      data: { session: null },
      error: { ...STUB_ERROR },
    }),
    signInWithPassword: async () => ({
      data: { user: null, session: null },
      error: { ...STUB_ERROR },
    }),
    signInWithOAuth: async () => ({
      data: { provider: null, url: null },
      error: { ...STUB_ERROR },
    }),
    signUp: async () => ({
      data: { user: null, session: null },
      error: { ...STUB_ERROR },
    }),
    signOut: async () => ({ error: null }),
    onAuthStateChange: (_cb: unknown) => ({
      data: { subscription: { unsubscribe: () => {} } },
    }),
    resetPasswordForEmail: async () => ({
      data: null,
      error: { ...STUB_ERROR },
    }),
    updateUser: async () => ({
      data: { user: null },
      error: { ...STUB_ERROR },
    }),
    setSession: async () => ({
      data: { session: null, user: null },
      error: { ...STUB_ERROR },
    }),
    refreshSession: async () => ({
      data: { session: null, user: null },
      error: { ...STUB_ERROR },
    }),
    verifyOtp: async () => ({
      data: { user: null, session: null },
      error: { ...STUB_ERROR },
    }),
    exchangeCodeForSession: async () => ({
      data: { session: null, user: null },
      error: { ...STUB_ERROR },
    }),
  },
  channel: (_name: string) => makeChannel(),
  removeChannel: (_ch: unknown) => {},
  removeAllChannels: () => {},
  getChannels: () => [],
  functions: {
    invoke: async (_name: string, _opts?: unknown) => stubResult(),
  },
  storage: {
    from: (_bucket: string) => makeStorageBucket(),
    listBuckets: async () => stubResult(),
    getBucket: async () => stubResult(),
    createBucket: async () => stubResult(),
    updateBucket: async () => stubResult(),
    deleteBucket: async () => stubResult(),
    emptyBucket: async () => stubResult(),
  },
};

export type SupabaseClient = typeof supabase;
export type { Database };
